import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import './index.less'

const VideoScrollerSwipper = ({ sectionId, showPagination = false, enableConfig = false }) => {
  const DomRef = useRef(null)
  const SwiperDom = useRef(null)
  const SwiperOb = useRef(null)
  const VirtualVideoDom = useRef(null)
  const VirtualVideoDoms = useRef(null)
  const isScrollingToTarget = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)
  
  // Convert enableConfig from string to boolean if needed
  const enableConfigBool = enableConfig === true || enableConfig === 'true'

  const changeSwipperSlide = () => {
    // Skip if we're programmatically scrolling to a target
    if (isScrollingToTarget.current) return
    
    if (!DomRef.current || !SwiperOb.current || !VirtualVideoDom.current) return
    
    const scrollTopPosition = window.scrollY
    const targetTopPosition = DomRef.current.offsetTop
    const virtualVideoHeight = VirtualVideoDom.current.offsetHeight
    
    // Use Math.round for more accurate index calculation
    const calculatedIndex = Math.round(
      (scrollTopPosition - targetTopPosition) / virtualVideoHeight
    )
    
    // Ensure index is within valid range
    const maxIndex = SwiperOb.current.slides.length - 1
    let index = calculatedIndex
    
    if (index < 0) {
      index = 0
    } else if (index > maxIndex) {
      index = maxIndex
    }
    
    const currentIndex = SwiperOb.current.activeIndex
    
    // Update swiper and activeIndex if index changed
    if (currentIndex !== index) {
      SwiperOb.current.slideTo(index, 300) // Add smooth transition speed
      setActiveIndex(index)
    }
  }

  useEffect(() => {
    DomRef.current = document.querySelector(`[section-id=${sectionId}]`)

    SwiperDom.current = DomRef.current.querySelector('.swiper')
    VirtualVideoDom.current = DomRef.current.querySelector('.virtual-video')
    VirtualVideoDoms.current = DomRef.current.querySelectorAll('.virtual-video')

    // Add click event handlers to title-desc-items
    const titleDescItems = DomRef.current.querySelectorAll('.title-desc-item')
    const clickHandlers = []
    
    titleDescItems.forEach((item, index) => {
      const handleItemClick = () => {
        if (!SwiperOb.current || !DomRef.current || !VirtualVideoDom.current) return
        
        // Set flag to prevent scroll listener from interfering
        isScrollingToTarget.current = true
        
        // Update swiper with smooth transition (speed in ms)
        SwiperOb.current.slideTo(index, 500) // 500ms smooth transition to match scroll duration
        setActiveIndex(index)
        
        // Scroll page to the corresponding virtual video position
        const targetTopPosition = DomRef.current.offsetTop
        const virtualVideoHeight = VirtualVideoDom.current.offsetHeight
        const targetScrollPosition = targetTopPosition + (index * virtualVideoHeight)
        
        window.scrollTo({
          top: targetScrollPosition,
          behavior: 'smooth'
        })
        
        // Reset flag after scroll animation completes
        // Smooth scroll typically takes 500-800ms, using 1200ms to be safe
        let scrollCheckCount = 0
        const maxChecks = 24 // ~1200ms at 50ms intervals
        const checkInterval = setInterval(() => {
          scrollCheckCount++
          const currentScroll = window.scrollY
          const scrollDiff = Math.abs(currentScroll - targetScrollPosition)
          
          // If scroll has stopped (within 5px) or max checks reached
          if (scrollDiff < 5 || scrollCheckCount >= maxChecks) {
            clearInterval(checkInterval)
            isScrollingToTarget.current = false
          }
        }, 50)
      }
      
      clickHandlers.push(handleItemClick)
      item.addEventListener('click', handleItemClick)
    })

    return () => {
      titleDescItems.forEach((item, index) => {
        if (clickHandlers[index]) {
          item.removeEventListener('click', clickHandlers[index])
        }
      })
    }
  }, [])

  useEffect(() => {
    if (!window?.XBLOOM3?.Swiper) return

    if (!SwiperDom.current) return
    
    // Recalculate mobile and horizontal mode inside useEffect
    const currentIsMobile = window.XBLOOM3?.IS_MOBILE || window.innerWidth <= 768
    const currentIsHorizontalMode = enableConfigBool && currentIsMobile
    
    const swiperPagination = showPagination
      ? {
          pagination: {
            el: SwiperDom.current.querySelector('.swiper-pagination'),
            clickable: true,
          },
        }
      : {}

    // Configure Swiper based on horizontal mode
    const swiperConfig = currentIsHorizontalMode
      ? {
          direction: 'horizontal',
          allowTouchMove: true,
          slidesPerView: '1',
          spaceBetween: 0,
          loop: false,
          ...swiperPagination,
          on: {
            slideChange: function () {
              const playVideo =
                this?.visibleSlides?.[0]?.querySelector('video') ?? {}
              playVideo.currentTime = 0
              setActiveIndex(this.activeIndex)
            },
          },
        }
      : {
          direction: 'vertical',
          allowTouchMove: false,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
          slidesPerView: '1',
          spaceBetween: 0,
          loop: false,
          ...swiperPagination,
          on: {
            slideChange: function () {
              const playVideo =
                this?.visibleSlides?.[0]?.querySelector('video') ?? {}
              playVideo.currentTime = 0
              setActiveIndex(this.activeIndex)
            },
          },
        }

    SwiperOb.current = new window.XBLOOM3.Swiper(SwiperDom.current, swiperConfig)

    if (!SwiperOb?.current) return

    // Only add scroll listener for vertical mode
    if (!currentIsHorizontalMode) {
      document.addEventListener('scroll', changeSwipperSlide)
      changeSwipperSlide()

      return () => {
        document.removeEventListener('scroll', changeSwipperSlide)
      }
    }
  }, [enableConfigBool, showPagination])

  // Update active class on title-desc-items based on activeIndex
  useEffect(() => {
    if (!DomRef.current) return

    const titleDescItems = DomRef.current.querySelectorAll('.title-desc-item')
    titleDescItems.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add('active')
      } else {
        item.classList.remove('active')
      }
    })
  }, [activeIndex])

  return <div></div>
}

window.XBLOOM3.DefineXbloomSectionReactComponent(
  'video-scroller-swipper',
  VideoScrollerSwipper,
  ReactDOM
)
